import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowArcLeft, ArrowArcRight, ArrowLeft, ArrowRight, ArrowSquareOut, X } from "@phosphor-icons/react"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { debounce } from "es-toolkit/function"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogOverlay, DialogPortal, DialogTitle } from "@/components/ui/dialog"
import { formatDate } from "@/lib/format-date"
import { AppointmentAttachment } from "@/modules/patients/components/attachments/attachment-item"
import { usePreviewDocumentDialogStore } from "@/stores/usePreviewDocumentDialogStore"

export const PreviewDocumentDialogWrapper = () => {
  const mediaFiles = usePreviewDocumentDialogStore((state) => state.mediaFiles)
  const previewDocumentAppointmentId = usePreviewDocumentDialogStore((state) => state.previewDocumentAppointmentId)

  if (mediaFiles) {
    return <PreviewDocumentDialog mediaFiles={mediaFiles} previewDocumentAppointmentId={previewDocumentAppointmentId} />
  }

  return null
}

type PreviewDocumentDialogProps = {
  mediaFiles: AppointmentAttachment[]
  isMediaFilesDataFromPrams?: boolean
  previewDocumentAppointmentId?: string
}

function computeScaleReality(img: HTMLImageElement) {
  // default scale value
  let scale = 2
  // extract the scale value from the parent's transform style in case vertical or horizontal media is bigger than the container when rotated.
  const values = img.parentElement!.style.transform.match(/scale\(([^)]+)\)/)
  if (values) {
    const containerScale = parseFloat(values[1])
    // adjust scale based on container scale in case it's less than 1
    if (containerScale < 1) scale += 1 - containerScale
  }
  return scale
}

export const PreviewDocumentDialog = (props: PreviewDocumentDialogProps) => {
  const { mediaFiles, isMediaFilesDataFromPrams, previewDocumentAppointmentId } = props
  const { t } = useTranslation()

  const index = usePreviewDocumentDialogStore((state) => state.index)
  const setMediaFiles = usePreviewDocumentDialogStore((state) => state.setMediaFiles)
  const setIndex = usePreviewDocumentDialogStore((state) => state.setIndex)
  const setPreviewDocumentAppointmentId = usePreviewDocumentDialogStore(
    (state) => state.setPreviewDocumentAppointmentId,
  )

  const previewMedia = mediaFiles[index]
  const length = mediaFiles.length ?? 0

  const [mediaSize, setMediaSize] = useState<{ width: number; height: number } | null>(null)
  const mediaContainerRef = useRef<HTMLDivElement>(null)
  const dialogContainerRef = useRef<HTMLDivElement>(null)
  const rotationRef = useRef(0)
  const zoomInRef = useRef(false)
  const zoomOutRef = useRef(false)

  const handleZoomStartDebounce = useRef(
    debounce((e: React.MouseEvent<HTMLElement>) => {
      zoomInRef.current = true
      if (!zoomOutRef.current) {
        const img = e.target as HTMLImageElement
        const scale = computeScaleReality(img)
        img.style.transform = `scale(${scale})`
        img.style.transition = "transform 0.3s ease"
        img.style.cursor = "crosshair"
      }
    }, 350),
  ).current

  useEffect(() => {
    const img = mediaContainerRef.current?.querySelector("img")
    const dialogEl = dialogContainerRef.current
    const containerEl = mediaContainerRef.current
    if (!img || !dialogEl || !containerEl) return

    const { naturalWidth, naturalHeight } = img
    const { width: renderedWidth, height: renderedHeight } = img.getBoundingClientRect()
    const { width: containerWidth, height: containerHeight } = containerEl.getBoundingClientRect()
    const { width: dialogWidth } = dialogEl.getBoundingClientRect()
    const restDialogWidth = dialogWidth - containerWidth
    const isRotated = rotationRef.current % 180 !== 0
    const rotatedRenderedWidth = isRotated ? renderedHeight : renderedWidth
    const rotatedRenderedHeight = isRotated ? renderedWidth : renderedHeight
    const isOverflowing =
      rotatedRenderedWidth > containerWidth || (isMediaFilesDataFromPrams && rotatedRenderedHeight > containerHeight)

    if (isRotated) {
      const width = isOverflowing ? containerWidth - restDialogWidth : renderedWidth
      const height = renderedWidth
      setMediaSize({ width, height })
    } else {
      setMediaSize({ width: naturalWidth, height: naturalHeight })
    }
  }, [isMediaFilesDataFromPrams])

  const handleLoadImage = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    setMediaSize({ width: img.naturalWidth, height: img.naturalHeight })
  }

  const handleLoadVideo = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget
    setMediaSize({ width: video.videoWidth, height: video.videoHeight })
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!mediaContainerRef.current || (!zoomInRef.current && !zoomOutRef.current)) return

    // Get the container's bounding rectangle
    const rect = mediaContainerRef.current.getBoundingClientRect()

    // Calculate the center point of the container
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    // Calculate the mouse offset from the center
    const offsetX = e.clientX - centerX
    const offsetY = e.clientY - centerY

    // Convert rotation from degrees to radians (negative because CSS rotation is clockwise)
    const angle = (-rotationRef.current * Math.PI) / 180

    // Apply inverse rotation to mouse coordinates to compensate for rotated image
    const rotatedX = offsetX * Math.cos(angle) - offsetY * Math.sin(angle)
    const rotatedY = offsetX * Math.sin(angle) + offsetY * Math.cos(angle)

    // Calculate the transform origin as a percentage based on rotated mouse position
    const xPercent = ((rotatedX + rect.width / 2) / rect.width) * 100
    const yPercent = ((rotatedY + rect.height / 2) / rect.height) * 100

    e.currentTarget.style.transformOrigin = `${xPercent}% ${yPercent}%`
    e.currentTarget.style.cursor = "crosshair"
  }

  const handleZoomEnd = (e: React.MouseEvent<HTMLElement>) => {
    zoomInRef.current = false
    zoomOutRef.current = true
    e.currentTarget.style.transform = "scale(1)"
    e.currentTarget.style.transition = "transform 0.3s ease"
    e.currentTarget.style.cursor = "default"
  }

  const handleRotate = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const button = event.currentTarget
    button.disabled = true
    button.style.cursor = "wait"
    const degreeValue = Number(button.getAttribute("data-degree") ?? "0")
    handleUpdateStyle((rotationRef.current += degreeValue))
    setTimeout(() => {
      button.disabled = false
      button.style.cursor = "default"
    }, 350)
  }

  const handleUpdateStyle = useCallback(
    (degree: number) => {
      if (!mediaContainerRef.current) return

      const { height, width } = mediaSize ?? { height: 0, width: 0 }
      const containerRect = mediaContainerRef.current.getBoundingClientRect()
      const grandParentRect = mediaContainerRef.current.parentElement!.getBoundingClientRect()
      const isRotated = degree % 180 !== 0
      let scale = 1

      if (isRotated) {
        if (grandParentRect.height < containerRect.width && height < width) {
          scale = grandParentRect.height / containerRect.width
        } else if (grandParentRect.width < containerRect.height && width < height) {
          scale = grandParentRect.width / containerRect.height
        } else if (grandParentRect.width > containerRect.height) {
          scale = (grandParentRect.width / containerRect.height) * 0.95
        }
      }

      mediaContainerRef.current.style.transform = `scale(${scale}) rotate(${degree}deg)`
      mediaContainerRef.current.style.transformOrigin = "center center"
      mediaContainerRef.current.style.transition = "transform 0.3s ease"
    },
    [mediaSize],
  )

  const showFullImage = () => {
    const currentUrl = window.location.origin

    const url = new URL(currentUrl)
    url.searchParams.set("previewDocumentAppointmentId", previewDocumentAppointmentId ?? "")
    url.searchParams.set("mediaFileId", previewMedia.id)
    url.searchParams.set("mode", "full")

    window.open(
      url.toString(),
      "_blank",
      "width=1920,height=1080,top=50,left=50,toolbar=no,menubar=no,scrollbars=yes,resizable=yes,location=no,status=no",
    )
  }

  const displayDataContent = (mediaFileData: AppointmentAttachment) => {
    const { url, attachmentType, fileName, uploadStatus } = mediaFileData
    if (!uploadStatus) return null

    if (uploadStatus === "UPLOADED")
      return (
        <Banner type="inline" className="w-auto min-w-80">
          {t("patient.uploadFiles.messages.virusCheckInProgress")}
        </Banner>
      )
    if (uploadStatus === "MALICIOUS") {
      return (
        <Banner type="inline" variant="most-critical" className="w-auto">
          {t("patient.uploadFiles.messages.fileUnsafeOrHarmful")}
        </Banner>
      )
    }
    if (uploadStatus === "ERROR")
      return (
        <Banner type="inline" variant="most-critical" className="w-auto">
          {t("patient.uploadFiles.messages.uploadFailedRetry")}
        </Banner>
      )

    if (uploadStatus === "SCAN_TIMEOUT")
      return (
        <Banner type="inline" variant="most-critical" className="w-auto">
          {t("patient.uploadFiles.messages.virusCheckFailedRetry")}
        </Banner>
      )

    switch (attachmentType) {
      case "AUDIO":
        return <audio className="object-contain" src={url} controls />
      case "VIDEO":
        return (
          <div ref={mediaContainerRef} className="relative flex h-full items-center justify-center overflow-hidden">
            <video
              className="max-h-full max-w-full object-contain"
              src={url}
              controls
              onLoadedMetadata={handleLoadVideo}
            />
          </div>
        )
      case "PHOTO":
        return (
          <div
            ref={mediaContainerRef}
            className="flex h-full w-full items-center justify-center overflow-visible"
            style={{
              transform: `scale(1) rotate(${rotationRef.current}deg)`,
              transition: "transform 0.3s ease",
            }}>
            <img
              src={url}
              alt={fileName}
              width={mediaSize?.width}
              height={mediaSize?.height}
              onMouseMove={handleMouseMove}
              onMouseEnter={(e) => {
                zoomOutRef.current = false
                handleZoomStartDebounce(e)
              }}
              onMouseOut={handleZoomEnd}
              className="max-h-full max-w-full object-contain"
              onLoad={handleLoadImage}
            />
          </div>
        )
      case "PDF":
        return (
          <embed className="h-full w-full object-contain" src={`${url}?embed=true#view=fitH`} type="application/pdf" />
        )
    }
  }

  return (
    <Dialog open={true}>
      <DialogPortal>
        <DialogOverlay
          onMouseDown={(e) => {
            e.stopPropagation()
            setMediaFiles(null)
          }}
        />
        <VisuallyHidden>
          <DialogTitle />
        </VisuallyHidden>
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseDown={(e) => e.stopPropagation()}
          ref={dialogContainerRef}
          className={isMediaFilesDataFromPrams ? "h-screen w-screen max-w-none rounded-none" : ""}>
          <div className="grid grid-cols-4 gap-6 px-6 pt-4 pb-6" data-testid="preview-document-dialog">
            <div
              className="bg-background-subdued relative col-span-3 flex items-center justify-center overflow-hidden rounded-xl"
              style={{ height: "calc(100vh - 6rem)" }}
              data-testid="document-content-section">
              {previewMedia && displayDataContent(previewMedia)}
            </div>
            <div
              className="grid gap-6"
              style={{ gridTemplateRows: "auto minmax(0, 1fr) auto" }}
              data-testid="document-information-section">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center">
                <h3
                  className="text-title overflow-hidden font-bold text-ellipsis whitespace-nowrap"
                  data-testid="document-file-name">
                  {previewMedia && previewMedia.fileName}
                </h3>
                {!isMediaFilesDataFromPrams && (
                  <Button
                    data-testid="close-preview-document-dialog-button"
                    onClick={() => {
                      setMediaFiles(null)
                      setPreviewDocumentAppointmentId(undefined)
                    }}
                    variant="circle"
                    className="text-on-surface-subdued h-10 w-10 cursor-pointer">
                    <X />
                  </Button>
                )}
              </div>
              <div className="text-on-surface-subdued grid grid-cols-[auto_auto_minmax(0,1fr)] content-start gap-x-6 gap-y-2">
                {previewMedia && previewMedia.uploadDate && (
                  <>
                    <span>{t("scheduling.waitingRoom.previewDocument.uploadedAt")}</span> <span>:</span>{" "}
                    <span className="text-on-surface text-sm font-medium">
                      {formatDate(new Date(previewMedia.uploadDate), "dd.MM.yyyy HH:mm:ss")}
                    </span>{" "}
                  </>
                )}
              </div>
              {(previewMedia.attachmentType === "PHOTO" || previewMedia.attachmentType === "VIDEO") && (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-row">
                    <Button variant="tertiary" data-degree="-90" onClick={handleRotate}>
                      <ArrowArcLeft size={20} />
                    </Button>
                    <Button variant="tertiary" data-degree="90" onClick={handleRotate}>
                      <ArrowArcRight size={20} />
                    </Button>
                  </div>
                  <div className="flex flex-row">
                    <Button variant="tertiary" onClick={showFullImage}>
                      {!isMediaFilesDataFromPrams && <ArrowSquareOut size={20} />}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                <Button
                  variant="circle"
                  data-testid="previous-document-button"
                  onClick={() => {
                    rotationRef.current = 0
                    setIndex(Math.max(index - 1, 0))
                    handleUpdateStyle(rotationRef.current)
                  }}
                  disabled={index <= 0}
                  className="border-border h-10 w-10 border">
                  <ArrowLeft size={20} />
                </Button>
                <span className="text-body-sm bg-surface-subdued text-on-surface flex items-center gap-2 rounded-md px-2 py-1 font-semibold">
                  <span>{index + 1}</span>
                  <span>/</span>
                  <span>{length}</span>
                </span>
                <Button
                  variant="circle"
                  data-testid="next-document-button"
                  onClick={() => {
                    rotationRef.current = 0
                    setIndex(Math.min(index + 1, length - 1))
                    handleUpdateStyle(rotationRef.current)
                  }}
                  disabled={index >= length - 1}
                  className="border-border h-10 w-10 border">
                  <ArrowRight size={20} />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
